import { PickingInfo } from "@deck.gl/core"
import { isValidAirspaceType } from "@nova/constants"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { throttle } from "nodash"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActivePopup, setHoveredAirspaces } from "features/overview/slice"
import { AIRSPACE_LAYER_IDS } from "./layerIds"
import { AirspaceFeatureRef, PolygonFeature } from "./types"

const HOVER_THROTTLE_MS = 60 // visually instant but prevents dispatching at 60fps

const toHoveredKey = (refs: AirspaceFeatureRef[]) =>
    refs
        .map((ref) => `${ref.type}:${ref.id}`)
        .sort()
        .join(",")

export const useAirspaceHoverHandler = () => {
    const dispatch = useAppDispatch()
    const shouldHighlightHovers = !(useAppSelector(selectActivePopup)?.type ?? "").startsWith(
        "airspace"
    )
    const lastHoveredKeyRef = useRef("")

    const dispatchHovered = useCallback(
        (hovered: AirspaceFeatureRef[]) => {
            const key = toHoveredKey(hovered)
            if (key === lastHoveredKeyRef.current) return
            lastHoveredKeyRef.current = key
            dispatch(setHoveredAirspaces(hovered))
        },
        [dispatch]
    )

    const pickHoveredAndDispatch = useCallback(
        (info: PickingInfo<PolygonFeature>) => {
            const deck = info.layer?.context.deck
            if (info.object?.id == null || !deck) return
            const hovered = deck
                .pickMultipleObjects({
                    x: info.x,
                    y: info.y,
                    radius: 2,
                    layerIds: AIRSPACE_LAYER_IDS,
                })
                .reduce((acc, cur) => {
                    const id = cur.object?.id
                    const type = cur.object?.properties?.featureType
                    if (!id || !isValidAirspaceType(type)) return acc
                    acc.push({ type, id: `${cur.object.id}` })
                    return acc
                }, [] as AirspaceFeatureRef[])
            dispatchHovered(hovered)
        },
        [dispatchHovered]
    )
    const throttledPickHoveredAndDispatch = useMemo(
        () => throttle(pickHoveredAndDispatch, HOVER_THROTTLE_MS),
        [pickHoveredAndDispatch]
    )
    useEffect(() => {
        return () => throttledPickHoveredAndDispatch.cancel()
    }, [throttledPickHoveredAndDispatch])

    return useCallback(
        (info: PickingInfo<PolygonFeature>) => {
            if (!shouldHighlightHovers) return
            if (info.object?.id == null) {
                throttledPickHoveredAndDispatch.cancel()
                dispatchHovered([])
                return
            }
            throttledPickHoveredAndDispatch(info)
        },
        [shouldHighlightHovers, throttledPickHoveredAndDispatch, dispatchHovered]
    )
}
